import * as THREE from "three";
import { Sfx } from "../audio/sfx";
import { assignKeys, normalizeKey } from "../math/keys";
import {
  buildFactBag,
  makeQuestion,
  pickFact,
  recordMastery,
  type Difficulty,
  type Mastery,
  type Question,
  type WeightedFact,
} from "../math/questions";
import { Engine } from "./engine";
import type { RunConfig, RunSummary } from "./session";
import { comboPoints, hullPips, padScore } from "./score";
import type { KeyMode } from "./save";
import { Swarm } from "./swarm";
import type { SwarmKind } from "./ships";
import { LANE_COLORS } from "./ships";
import { World } from "./world";

export const SWEEP_THREAT: Record<Difficulty, { spawn: number; speed: number; hp: number }> = {
  cadet: { spawn: 1.85, speed: 6.2, hp: 5 },
  pilot: { spawn: 1.28, speed: 8.4, hp: 5 },
  ace: { spawn: 0.88, speed: 11.2, hp: 3 },
};

export interface SweepConfig extends RunConfig {
  keyMode: KeyMode;
}

export class SweepSession {
  readonly engine: Engine;
  readonly world: World;
  readonly sfx: Sfx;
  readonly swarm: Swarm;
  private bag: WeightedFact[] = [];
  private mastery = new Map<string, Mastery>();
  private question: Question | null = null;
  private lockedKeys: string[] | undefined;
  private askedAt = 0;
  private spawnT = 0;
  private thunderT = 8;
  private hull = 5;
  private hullMax = 5;
  private score = 0;
  private streak = 0;
  private streakMax = 0;
  private hits = 0;
  private misses = 0;
  private fastestMs = Number.POSITIVE_INFINITY;
  private wave = 1;
  private playing = false;
  private paused = false;
  private iFrames = 0;
  private spawnProtect = 0;
  private best = 0;
  private config: SweepConfig | null = null;
  private steerX = 0;
  private steerZ = 0.6;
  private callout = "";
  private onHud: (() => void) | null = null;
  private onOver: ((summary: RunSummary) => void) | null = null;
  private mobile: boolean;

  constructor(engine: Engine, world: World, sfx: Sfx) {
    this.engine = engine;
    this.world = world;
    this.sfx = sfx;
    this.swarm = new Swarm(engine.scene);
    this.mobile = window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 820;
    this.swarm.setActive(false);
  }

  bind(hud: () => void, over: (summary: RunSummary) => void): void {
    this.onHud = hud;
    this.onOver = over;
  }

  get playerX(): number {
    return this.swarm.playerX;
  }

  snapshot() {
    return {
      playing: this.playing,
      paused: this.paused,
      hull: this.hull,
      hullMax: this.hullMax,
      hullText: hullPips(this.hull, this.hullMax),
      score: this.score,
      scoreText: padScore(this.score),
      streak: this.streak,
      wave: this.wave,
      question: this.question,
      callout: this.callout,
    };
  }

  setActive(active: boolean): void {
    this.swarm.setActive(active);
    if (!active) {
      this.playing = false;
      this.paused = false;
      this.question = null;
    }
  }

  start(config: SweepConfig, best = 0): void {
    this.config = config;
    this.best = best;
    this.bag = buildFactBag(config.tables, config.squaresOnly);
    this.mastery = new Map();
    const tune = SWEEP_THREAT[config.difficulty];
    this.hullMax = tune.hp;
    this.hull = tune.hp;
    this.score = 0;
    this.streak = 0;
    this.streakMax = 0;
    this.hits = 0;
    this.misses = 0;
    this.fastestMs = Number.POSITIVE_INFINITY;
    this.wave = 1;
    this.playing = true;
    this.paused = false;
    this.spawnT = 0.85;
    this.iFrames = 0;
    this.spawnProtect = 2.4;
    this.lockedKeys = undefined;
    this.steerX = 0;
    this.steerZ = 0.6;
    this.callout = "HOLD THE GRID";
    this.swarm.reset();
    this.swarm.setActive(true);
    this.nextQuestion();
    this.onHud?.();
  }

  abort(): void {
    this.playing = false;
    this.paused = false;
    this.swarm.reset();
    this.question = null;
    this.callout = "";
    this.onHud?.();
  }

  setPaused(paused: boolean): void {
    if (!this.playing) return;
    this.paused = paused;
    this.onHud?.();
  }

  setSteer(x: number, z: number): void {
    this.steerX = x;
    this.steerZ = z;
  }

  nudge(dx: number, dz: number): void {
    this.steerX = THREE.MathUtils.clamp(this.steerX + dx, -1, 1);
    this.steerZ = THREE.MathUtils.clamp(this.steerZ + dz, -1.2, 2.8);
  }

  handleKey(raw: string): void {
    if (!this.playing || this.paused) return;
    const key = normalizeKey(raw);
    if (key === "arrowleft") {
      this.nudge(-0.22, 0);
      return;
    }
    if (key === "arrowright") {
      this.nudge(0.22, 0);
      return;
    }
    if (key === "arrowup") {
      this.nudge(0, -0.45);
      return;
    }
    if (key === "arrowdown") {
      this.nudge(0, 0.45);
      return;
    }
    if (key.length !== 1 || key < "a" || key > "z") return;
    this.answer(key);
  }

  answer(key: string): boolean {
    if (!this.playing || this.paused || !this.question) return false;
    const choice = this.question.choices.find((c) => c.key === key);
    if (!choice) return false;
    if (choice.correct) this.onCorrect();
    else this.onWrong();
    return true;
  }

  simulate(dt: number): number {
    const playing = this.playing && !this.paused;
    const drive = playing ? 1 + this.wave * 0.12 : this.playing ? 0 : 0.7;

    if (playing) this.swarm.setTarget(this.steerX * 9, this.steerZ);
    else this.swarm.setTarget(Math.sin(performance.now() * 0.00045) * 4, 0.6);

    this.spawnT -= playing ? dt : 0;
    if (playing && this.spawnT <= 0 && this.swarm.enemies.length < (this.mobile ? 8 : 12)) {
      this.spawnEnemy();
      const tune = SWEEP_THREAT[this.config?.difficulty ?? "pilot"];
      const interval = Math.max(0.28, tune.spawn * Math.pow(0.92, this.wave - 1));
      this.spawnT = interval * (0.75 + Math.random() * 0.5);
    }

    const sim = this.paused ? 0 : playing ? dt : dt * 0.55;
    const { collisions } = this.swarm.update(sim);
    if (playing) {
      this.iFrames = Math.max(0, this.iFrames - dt);
      this.spawnProtect = Math.max(0, this.spawnProtect - dt);
      this.swarm.blink(this.iFrames);
      for (const enemy of collisions) {
        this.swarm.kill(enemy, 0xffb347);
        if (this.iFrames > 0 || this.spawnProtect > 0) continue;
        this.streak = 0;
        this.hull = Math.max(0, this.hull - (enemy.kind === "heavy" ? 2 : 1));
        this.iFrames = 1.15;
        this.sfx.explosion();
        this.sfx.damage();
        this.engine.addShake(0.4);
        this.flash("bad");
        this.callout = "COLLISION";
        this.onHud?.();
        if (this.hull <= 0) {
          this.gameOver();
          break;
        }
      }
    } else {
      this.swarm.blink(0);
    }

    this.thunderT -= dt;
    if (this.thunderT <= 0) {
      this.thunderT = 10 + Math.random() * 10;
      this.engine.addShake(0.12);
      if (playing) this.sfx.thunder();
    }

    return drive;
  }

  private nextQuestion(): void {
    if (!this.config || !this.playing) return;
    const keys = assignKeys(4, this.config.deck, Math.random, this.config.keyMode === "lock" ? this.lockedKeys : undefined);
    if (this.config.keyMode === "lock") this.lockedKeys = keys;
    const fact = pickFact(this.bag, this.mastery);
    this.question = makeQuestion(fact, keys);
    this.world.setQuestion(this.question.a, this.question.b);
    this.askedAt = performance.now();
  }

  private onCorrect(): void {
    const q = this.question;
    if (!q) return;
    const elapsed = performance.now() - this.askedAt;
    this.hits += 1;
    this.streak += 1;
    this.streakMax = Math.max(this.streakMax, this.streak);
    this.fastestMs = Math.min(this.fastestMs, elapsed);
    this.score += comboPoints(elapsed, this.streak, this.wave);
    recordMastery(this.mastery, q.a, q.b, true, elapsed);
    this.sfx.laser();
    this.sfx.correct();
    if (this.streak % 5 === 0) this.sfx.combo(this.streak / 5);
    this.callout = elapsed < 450 ? "PERFECT LOCK" : this.streak >= 5 ? `OVERCHARGE x${this.streak}` : "PRODUCT CONFIRMED";
    const chain = Math.min(8, 3 + Math.floor(this.streak / 3));
    const targets = this.swarm.nearest(chain);
    const pts = targets.map((e) => e.group.position.clone());
    if (!pts.length) pts.push(new THREE.Vector3(this.swarm.player.position.x, 1.2, -18));
    this.swarm.fireAt(pts, this.streak >= 8 ? LANE_COLORS[1] : LANE_COLORS[0]);
    for (const e of targets) {
      this.swarm.kill(e);
      this.score += 25 * this.streak;
    }
    this.engine.addShake(0.08);
    this.flash("good");
    if (this.hits % 8 === 0) this.wave += 1;
    this.nextQuestion();
    this.onHud?.();
  }

  private onWrong(): void {
    const q = this.question;
    if (!q) return;
    this.misses += 1;
    this.streak = 0;
    this.hull = Math.max(0, this.hull - 1);
    recordMastery(this.mastery, q.a, q.b, false, 4000);
    this.sfx.wrong();
    this.sfx.damage();
    this.swarm.explodeAt(this.swarm.player.position.clone(), 0xff3b5c);
    this.engine.addShake(0.32);
    this.callout = "FALSE PRODUCT";
    this.flash("bad");
    this.onHud?.();
    if (this.hull <= 0) this.gameOver();
  }

  private spawnEnemy(): void {
    const tune = SWEEP_THREAT[this.config?.difficulty ?? "pilot"];
    const roll = Math.random();
    const kind: SwarmKind = roll > 0.86 ? "heavy" : roll > 0.55 ? "striker" : "drone";
    const speed = tune.speed * (1 + (this.wave - 1) * 0.08) * (kind === "striker" ? 1.35 : kind === "heavy" ? 0.72 : 1);
    this.swarm.spawn(kind, speed);
  }

  private flash(kind: "good" | "bad"): void {
    const el = document.getElementById("flash");
    if (!el) return;
    el.classList.toggle("good", kind === "good");
    el.classList.add("on");
    window.setTimeout(() => el.classList.remove("on"), 90);
  }

  private gameOver(): void {
    this.playing = false;
    this.sfx.alarm();
    const attempts = this.hits + this.misses;
    const summary: RunSummary = {
      score: this.score,
      best: Math.max(this.best, this.score),
      hits: this.hits,
      misses: this.misses,
      leaked: 0,
      streakMax: this.streakMax,
      fastestMs: Number.isFinite(this.fastestMs) ? this.fastestMs : 0,
      accuracy: attempts === 0 ? 0 : this.hits / attempts,
      wave: this.wave,
      mastery: this.mastery,
    };
    this.onOver?.(summary);
  }
}
